'use client';

import { useTranslations } from 'next-intl';
import Image from 'next/image';
import React, { FC, useMemo } from 'react';
import { twMerge } from 'tailwind-merge';

import { Gender, PhotoSession, StoryLine } from '@/.gql/graphql';
import { useStyleContext, useProfileContext } from '@/src/providers';

type TExamplesItem = PhotoSession | StoryLine;

const styles = {
  example: 'w-full aspect-square object-cover object-top rounded-lg',
  exampleStoryline: 'aspect-[16/9]',
  examples: 'grid grid-cols-2 row-auto gap-x-4 gap-y-4 justify-between mb-4',
  storylineGrid: 'grid-cols-1',
  chooseTitle: 'text-base font-bold mb-3',
  exampleTitle: 'mb-4',
};

export const Examples: FC = () => {
  const t = useTranslations('imageModal');
  const { activeStyle, genderMode } = useStyleContext();
  const profile = useProfileContext()!;

  const examples = useMemo(() => {
    if (!['StoryLine', 'PhotoSession'].includes(activeStyle?.__typename!)) {
      return [];
    }

    return genderMode === Gender.Female
      ? (activeStyle as TExamplesItem)?.examplesWoman
      : (activeStyle as TExamplesItem)?.examplesMan;
  }, [activeStyle, genderMode]);

  const isStoryLine = activeStyle?.__typename! === 'StoryLine';

  if (!examples || !examples.length) {
    return;
  }

  return (
    <>
      <div className={twMerge(styles.chooseTitle, styles.exampleTitle)}>
        {activeStyle?.__typename! === 'StoryLine'
          ? t('examplesStorylinesTitle')
          : t('examplesTitle')}
      </div>
      <div
        className={twMerge(
          styles.examples,
          isStoryLine && styles.storylineGrid,
        )}
      >
        {examples.map((example, i) => (
          <button
            key={example}
            data-example-id={example}
            // onClick={handleClickExample}
          >
            {isStoryLine ? (
              <video
                key={example}
                autoPlay
                loop
                muted
                className={twMerge(styles.example, styles.exampleStoryline)}
                playsInline
                webkit-playsinline="true"
              >
                <source src={example} />
              </video>
            ) : (
              <Image
                width={152}
                height={180}
                src={example}
                alt={activeStyle?.name[profile?.lang]}
                className={styles.example}
                quality={95}
              />
            )}
          </button>
        ))}
      </div>
    </>
  );
};
